import * as Sentry from '@sentry/react';
import axios from 'axios';
import { isValid, parseISO } from 'date-fns';

import { auth } from '../firebase/firebase';

/**
 * Matches ISO 8601 date and date-time strings:
 * - Date only: 2024-01-15
 * - Date-time: 2024-01-15T10:30:00
 * - With milliseconds: 2024-01-15T10:30:00.123
 * - With timezone: 2024-01-15T10:30:00Z or 2024-01-15T10:30:00+10:00
 */
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;

/**
 * JSON reviver function that converts ISO 8601 date strings to Date objects.
 * Throws if a string matches the ISO date pattern but fails to parse.
 */
function reviveDates(key: string, value: unknown): unknown {
  if (typeof value !== 'string' || !ISO_DATE_REGEX.test(value)) {
    return value;
  }

  const date = parseISO(value);
  if (!isValid(date)) {
    throw new Error(`Invalid date value for field "${key}": "${value}"`);
  }
  return date;
}

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
  // Transform JSON responses to automatically parse ISO date strings into Date objects
  transformResponse: [
    (data: unknown) => {
      if (typeof data === 'string') {
        try {
          return JSON.parse(data, reviveDates);
        } catch {
          return data;
        }
      }
      return data;
    },
  ],
});

// Request interceptor for adding auth tokens, logging, etc.
apiClient.interceptors.request.use(
  async (config) => {
    // Add Firebase auth token if available
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to get auth token:', error);
      }
    }

    if (import.meta.env.DEV) {
      console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    }
    return config;
  },
  (error) => {
    if (import.meta.env.DEV) {
      console.error('Request interceptor error:', error);
    }
    return Promise.reject(error);
  },
);

// Response interceptor for global error handling and data transformation
apiClient.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log(`Response from ${response.config.url}:`, response.status);
    }
    return response;
  },
  async (error) => {
    // A request that asked for a blob gets its error body as a Blob too, which
    // logs and reports to Sentry as an opaque object. Read it back to real data.
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text().catch(() => '');
      try {
        error.response.data = JSON.parse(text);
      } catch {
        error.response.data = text;
      }
    }

    // Global error handling
    if (import.meta.env.DEV) {
      console.error('API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });
    }

    // Handle authentication and authorization errors
    const status = error.response?.status;
    if (status === 401) {
      console.warn('Authentication expired - signing out');
      // Clear auth state - ProtectedRoute will handle redirect via onAuthStateChanged
      auth.signOut().catch((signOutError) => {
        console.error('Error signing out:', signOutError);
      });
    } else if (status === 403) {
      // 403 means authenticated but not authorized - don't redirect, let the UI handle it
      console.error('Access denied - insufficient permissions');
    }

    // Report server errors (5xx) and unexpected client errors to Sentry
    if (!status || status >= 500) {
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status,
          responseData: error.response?.data,
        },
      });
    }

    return Promise.reject(error);
  },
);

/**
 * Custom axios instance for orval-generated API client.
 * This function is used as a mutator for all generated API calls.
 */
export const customInstance = async <T>(config: {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Override the 10s instance default for endpoints that do bulk work. */
  timeout?: number;
}): Promise<T> => {
  const response = await apiClient.request<T>({
    url: config.url,
    method: config.method,
    params: config.params,
    data: config.data,
    headers: config.headers,
    signal: config.signal,
    ...(config.timeout != null && { timeout: config.timeout }),
  });
  return response.data;
};
